import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowRight, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/auth/AuthShell";
import { usePermissionRedirect } from "@/hooks/usePermissionRedirect";
import { getCurrentUser } from "@/lib/authUser";

export default function JoinGroup() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { checkGroupLimit } = usePermissionRedirect();
  const [loading, setLoading] = useState(true);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!user) {
          navigate(`/login?redirect=/join/${inviteCode}`);
          return;
        }
        const { data: validation, error: inviteErr } = await supabase
          .rpc("validate_invite_code", { invite_code_input: inviteCode });
        const result = Array.isArray(validation) ? validation[0] : validation;
        if (inviteErr || !result?.is_valid) {
          setError("This invite link is invalid or has expired");
          setLoading(false);
          return;
        }
        setGroupName(result.group_name || "Unknown group");
      } catch (e: any) {
        setError("Failed to check invite");
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteCode, navigate]);

  const join = async () => {
    setLoading(true);
    if (!(await checkGroupLimit())) return setLoading(false);
    try {
      const { data, error: err } = await supabase.rpc("join_group_with_invite", {
        invite_code_param: inviteCode,
      });
      if (err) {
        if (err.message.includes("Already a member")) {
          toast.success("You're already a member of this group");
          const { data: v } = await supabase
            .rpc("validate_invite_code", { invite_code_input: inviteCode });
          const r = Array.isArray(v) ? v[0] : v;
          if (r?.group_id) return navigate(`/dashboard/groups/${r.group_id}/hot`);
        } else if (err.message.includes("Invalid or expired")) {
          toast.error("This invite link is invalid or has expired");
        } else {
          throw err;
        }
      } else {
        toast.success("Joined the group");
        const r = data as { group_id?: string };
        if (r?.group_id) return navigate(`/dashboard/groups/${r.group_id}/hot`);
      }
      navigate("/dashboard/personal/hot");
    } catch (e: any) {
      toast.error("Failed to join group", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !error && !groupName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Loading invite…
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{error ? "Invalid invite" : `Join ${groupName ?? "group"}`} | EchoCRM</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <AuthShell
        eyebrow={error ? "Invite" : "Group invite"}
        title={error ? "We couldn't open this invite." : `Join ${groupName}.`}
        subtitle={
          error
            ? error
            : "You'll get access to this group's shared pipeline, leads, and timelines."
        }
      >
        {error ? (
          <Button size="lg" className="w-full" onClick={() => navigate("/dashboard/personal/hot")}>
            Go to dashboard <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-border bg-secondary">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-[14px] font-medium">{groupName}</span>
            </div>
            <Button size="lg" className="w-full" onClick={join} disabled={loading}>
              {loading ? "Joining…" : <>Join group <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </div>
        )}
      </AuthShell>
    </>
  );
}